import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Logo } from "@/components/public/Layout";

function AuthShell({ title, subtitle, children, testId }) {
  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-16" data-testid={testId}>
      <div className="glow-violet pointer-events-none absolute -top-40 left-[-10%] h-[600px] w-[600px]" />
      <div className="glow-cyan pointer-events-none absolute bottom-[-20%] right-[-10%] h-[500px] w-[500px]" />
      <div className="relative w-full max-w-md">
        <div className="mb-10 flex items-center justify-between"><Logo /><Link to="/" className="eyebrow hover:text-white">← Website</Link></div>
        <div className="glass rounded-lg p-7 sm:p-9">
          <p className="eyebrow">{subtitle}</p>
          <h1 className="display-md mt-3 text-white">{title}</h1>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage({ admin = false }) {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) nav(homeFor(user), { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const u = await login(form.email, form.password); nav(homeFor(u), { replace: true }); }
    catch (ex) { setErr(errorMessage(ex)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title={admin ? "Admin sign in" : "Client sign in"} subtitle={admin ? "Private Admin Panel" : "Client Portal"} testId={admin ? "admin-login-page" : "login-page"}>
      <form onSubmit={submit} className="space-y-5" noValidate>
        <label className="block"><span className="eyebrow mb-2 block">Email</span><input type="email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" required data-testid="login-email" /></label>
        <label className="block"><span className="eyebrow mb-2 block">Password</span><input type="password" className="field" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" required data-testid="login-password" /></label>
        {err && <p className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="login-error">{err}</p>}
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="login-submit">{busy ? "Signing in…" : "Sign in"} <ArrowUpRight className="h-4 w-4" /></button>
        <div className="flex justify-between text-xs text-[var(--text-dim)]"><Link to="/forgot-password" className="hover:text-white" data-testid="login-forgot">Forgot password?</Link><span>{admin ? "Authorised staff only." : "Need access? Contact your project manager."}</span></div>
      </form>
    </AuthShell>
  );
}

export function InvitePage() {
  const { token } = useParams();
  const { acceptInvite } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/auth/invite/${token}`).then(({ data }) => { setInfo(data); setForm((f) => ({ ...f, name: data.name })); }).catch((e) => setErr(errorMessage(e))); }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.password !== form.confirm) return setErr("Passwords do not match");
    setBusy(true);
    try { await acceptInvite({ token, name: form.name, password: form.password }); nav("/client", { replace: true }); }
    catch (ex) { setErr(errorMessage(ex)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Create your account" subtitle="Client Invitation" testId="invite-page">
      {!info && !err && <p className="text-sm text-[var(--text-dim)]">Validating invitation…</p>}
      {err && !info && <p className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="invite-error">{err}</p>}
      {info && (
        <form onSubmit={submit} className="space-y-5" noValidate>
          <p className="text-sm text-[var(--text-dim)]">You have been invited to the RAHI DEV client portal{info.company ? ` for ${info.company}` : ""}. Account email: <span className="text-white">{info.email}</span></p>
          <label className="block"><span className="eyebrow mb-2 block">Your name</span><input className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="invite-name" /></label>
          <label className="block"><span className="eyebrow mb-2 block">Password (min 8 characters)</span><input type="password" className="field" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="invite-password" /></label>
          <label className="block"><span className="eyebrow mb-2 block">Confirm password</span><input type="password" className="field" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required data-testid="invite-confirm" /></label>
          {err && <p className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="invite-error">{err}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="invite-submit">{busy ? "Creating…" : "Create account"} <ArrowUpRight className="h-4 w-4" /></button>
        </form>
      )}
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e) => { e.preventDefault(); try { const { data } = await api.post("/auth/forgot-password", { email }); setMsg(data.message); } catch (ex) { setMsg(errorMessage(ex)); } };
  return (
    <AuthShell title="Reset access" subtitle="Forgot password" testId="forgot-page">
      <form onSubmit={submit} className="space-y-5">
        <label className="block"><span className="eyebrow mb-2 block">Email</span><input type="email" className="field" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" /></label>
        {msg && <p className="text-sm text-[var(--text-dim)]" data-testid="forgot-message">{msg}</p>}
        <button className="btn-primary w-full justify-center" data-testid="forgot-submit">Request reset link</button>
        <Link to="/login" className="block text-center text-xs text-[var(--text-dim)] hover:text-white">Back to sign in</Link>
      </form>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e) => { e.preventDefault(); try { await api.post("/auth/reset-password", { token: params.get("token"), password }); nav("/login"); } catch (ex) { setMsg(errorMessage(ex)); } };
  return (
    <AuthShell title="Choose a new password" subtitle="Reset password" testId="reset-page">
      <form onSubmit={submit} className="space-y-5">
        <label className="block"><span className="eyebrow mb-2 block">New password</span><input type="password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="reset-password" /></label>
        {msg && <p className="text-sm text-red-300" data-testid="reset-error">{msg}</p>}
        <button className="btn-primary w-full justify-center" data-testid="reset-submit">Update password</button>
      </form>
    </AuthShell>
  );
}
